import { DigitalHospitalSteps } from '@/modules/companyDigitalHospitalConfig/entities/enums/digitalHospital.enums';
import { cloneDeep, intersection, uniq } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export interface UseDigitalHospitalSelectedCompanyIdsProps {
  stepDigitalHospital: DigitalHospitalSteps;
}

export const useDigitalHospitalSelectedCompanyIds = ({
  stepDigitalHospital,
}: UseDigitalHospitalSelectedCompanyIdsProps) => {
  const initialState: Record<DigitalHospitalSteps, string[]> = Object.values(
    DigitalHospitalSteps,
  ).reduce(
    (acc, status) => ({ ...acc, [status]: [] }),
    {} as Record<DigitalHospitalSteps, string[]>,
  );

  const [selectedCompanyIdsByStep, setSelectedCompanyIdsByStep] =
    useState<Record<DigitalHospitalSteps, string[]>>(initialState);
  const [companyIdsOnPageByStep, setCompanyIdsOnPageByStep] =
    useState<Record<DigitalHospitalSteps, string[]>>(initialState);

  const selectedCompanyIdsOnPage = useMemo(
    () =>
      intersection(
        companyIdsOnPageByStep[stepDigitalHospital],
        selectedCompanyIdsByStep[stepDigitalHospital],
      ),
    [companyIdsOnPageByStep, stepDigitalHospital, selectedCompanyIdsByStep],
  );

  const updateCompanyIds = useCallback(
    (selected: boolean, stepDigitalHospital: DigitalHospitalSteps, companyIds: string[]) => {
      setSelectedCompanyIdsByStep((current) => {
        const clonedCurrent: string[] = uniq(cloneDeep(current[stepDigitalHospital]));

        if (selected) {
          return {
            ...current,
            [stepDigitalHospital]: uniq([...clonedCurrent, ...companyIds]),
          };
        }

        companyIds.forEach((companyId) => {
          const companyIdIndex = clonedCurrent.findIndex(
            (currentCompanyId) => currentCompanyId === companyId,
          );

          if (companyIdIndex > -1) {
            clonedCurrent.splice(companyIdIndex, 1);
          }
        });

        return { ...current, [stepDigitalHospital]: clonedCurrent };
      });
    },
    [],
  );

  return {
    selectedCompanyIdsByStep,
    setSelectedCompanyIdsByStep,
    companyIdsOnPageByStep,
    setCompanyIdsOnPageByStep,
    selectedCompanyIdsOnPage,
    updateCompanyIds,
  };
};
